import type { ImageSource } from './types';
import type { FC, PropsWithChildren } from 'react';

import { Image as DCImage } from 'dc-delivery-sdk-js/build/main/lib/media/Image';
import { ImageUrlBuilder } from 'dc-delivery-sdk-js/build/main/lib/media/ImageUrlBuilder';
import { useMemo } from 'react';

import { srcSizes } from './constants';
import { TileImageProvider } from './TileImageContext';
import type { Image, ImageLink } from '~/amplienceTypes/schemas/exported/product-schema';

interface AmplienceImageProviderProps {
  primaryImage?: Image;
  hoverImage?: Image;
}

export const AmplienceImageProvider: FC<PropsWithChildren<AmplienceImageProviderProps>> = ({
  children,
  primaryImage,
  hoverImage,
}) => {
  const value = useMemo(() => {
    const buildImageSource = (imageData?: ImageLink): ImageSource | null => {
      if (!imageData) {
        return null;
      }

      const image = new DCImage(imageData, {});

      if (!image) {
        return null;
      }

      const src = new ImageUrlBuilder(image)
        .width(1000)
        .parameter('sm', 'aspect')
        .parameter('aspect', '3:4')
        .parameter('fmt', 'auto')
        .build();

      const srcSet = srcSizes
        .map(
          size =>
            `${new ImageUrlBuilder(image)
              .width(size)
              .parameter('sm', 'aspect')
              .parameter('aspect', '3:4')
              .parameter('fmt', 'auto')
              .build()} ${size}w`
        )
        .join(', ');

      return { src, srcSet };
    };

    const primaryImageSource = primaryImage?.override?.[0]
      ? buildImageSource(primaryImage.override[0])
      : null;

    const hoverImageSource = hoverImage?.override?.[0]
      ? buildImageSource(hoverImage.override[0])
      : null;

    return {
      primaryImage: primaryImageSource,
      hoverImage: hoverImageSource,
    };
  }, [primaryImage, hoverImage]);

  return <TileImageProvider value={value}>{children}</TileImageProvider>;
};
